import React from 'react';
import { Box, IconButton, Paper, Menu, Divider, Typography, styled, useTheme, alpha, Tooltip, Grid } from '@mui/material';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import WaterIcon from '@mui/icons-material/Water';
import ForestIcon from '@mui/icons-material/Forest';
import { useThemeContext, type ThemeVariant } from './theme';

// Styled components
const ColorSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'backgroundColor' && prop !== 'isSelected',
})<{ backgroundColor: string; isSelected: boolean }>(({ theme, backgroundColor, isSelected }) => ({
  width: 24,
  height: 24,
  borderRadius: '50%',
  backgroundColor,
  border: isSelected 
    ? `2px solid ${theme.palette.primary.main}` 
    : `1px solid ${theme.palette.divider}`,
  boxShadow: isSelected 
    ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${alpha(theme.palette.primary.main, 0.3)}` 
    : 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const themeOptions: { 
  name: string; 
  value: ThemeVariant; 
  color: string; 
  icon: React.ReactNode;
  textColor?: string;
  iconColor?: string;
}[] = [
  { 
    name: 'Light', 
    value: 'light', 
    color: '#F8F9FE', 
    icon: <LightModeIcon />, 
    textColor: '#121212',
    iconColor: '#f39c12'
  },
  { 
    name: 'Dark', 
    value: 'dark', 
    color: '#1E1E1E', 
    icon: <DarkModeIcon />,
    textColor: '#FFFFFF',
    iconColor: '#3498db'
  },
  { 
    name: 'Midnight', 
    value: 'midnight', 
    color: '#111627', 
    icon: <NightsStayIcon />,
    textColor: '#FFFFFF', 
    iconColor: '#9b59b6'
  },
  { 
    name: 'Sunset', 
    value: 'sunset', 
    color: '#272636', 
    icon: <WbTwilightIcon />,
    textColor: '#FFFFFF',
    iconColor: '#e74c3c'
  },
  { 
    name: 'Ocean', 
    value: 'ocean', 
    color: '#182731', 
    icon: <WaterIcon />,
    textColor: '#FFFFFF',
    iconColor: '#2980b9'
  },
  { 
    name: 'Forest', 
    value: 'forest', 
    color: '#132713', 
    icon: <ForestIcon />,
    textColor: '#FFFFFF',
    iconColor: '#27ae60'
  },
];

export const ThemePicker: React.FC = () => {
  const theme = useTheme();
  const { theme: currentTheme, changeTheme } = useThemeContext();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleThemeChange = (newTheme: ThemeVariant) => {
    // If we're selecting the same theme currently active, just close the menu
    if (currentTheme === newTheme) {
      handleClose();
      return;
    }
    
    // Apply the new theme
    changeTheme(newTheme);
    
    // Close the menu
    handleClose();
    
    // Give visual feedback
    console.log(`Theme changed to ${newTheme}`);
  };

  return (
    <>
      <Tooltip title="Change theme">
        <IconButton 
          size="small" 
          onClick={handleClick}
          color="primary"
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.2),
            }
          }}
        >
          <ColorLensIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 280,
            borderRadius: 2,
          }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
          Choose a Theme
        </Typography>
        <Divider sx={{ mb: 1 }} />
        
        <Grid container spacing={1} sx={{ px: 2, pb: 2 }}>
          {themeOptions.map((option) => (
            <Grid item xs={6} key={option.value}>
              <Paper
                elevation={currentTheme === option.value ? 4 : 1}
                sx={{ 
                  p: 1.5,
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: 'pointer',
                  height: '80px',
                  backgroundColor: option.color,
                  border: currentTheme === option.value 
                    ? `2px solid ${theme.palette.primary.main}` 
                    : '2px solid transparent',
                  '&:hover': {
                    boxShadow: theme.shadows[4]
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => handleThemeChange(option.value)}
              >
                <Box 
                  sx={{ 
                    color: option.iconColor || (option.value === 'light' ? '#000' : '#fff'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                  }}
                >
                  {option.icon}
                </Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mt: 1, 
                    fontWeight: currentTheme === option.value ? 'bold' : 'normal',
                    color: option.textColor || (option.value === 'light' ? '#000' : '#fff'),
                    textShadow: option.value === 'light' ? 'none' : '0px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  {option.name}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Menu>
    </>
  );
};
